/*
 * Step: product.intro (/product). The opening of the Product section: a ~6.7 s
 * cinematic scene (authored on a 20 s storyboard, played 3x) drawn in code. A field of twinkling stars; a glowing engine
 * at the centre, pulsing with a bloom; six small, wordless "planets" (a report
 * page, a score ring, a chat, a message, a chart, a checklist) fade in and
 * settle into orbit around it; the engine pulses a signal out to each of them;
 * slow light streaks sweep across; the whole scene pushes in like a camera
 * (scale 1 to 1.08), the outer orbits a little faster for depth; one line of
 * copy rises; a final glow and lens flare. Then the page moves on to the next
 * step by itself, 1 s after the scene ends.
 *
 * It never traps or surprises the visitor:
 * - "Pause intro" stops the scene (CSS animation-play-state) and the timer;
 *   "Play intro" continues the remaining time. "Skip intro" goes on now. The
 *   step bar's Next is always there.
 * - Under prefers-reduced-motion the scene is drawn at rest (state "ended")
 *   and nothing moves on by itself.
 * - Every forward arrival (Next from the previous step, a nav or Contents
 *   link, a direct load) plays it. Arriving by going back does not bounce the
 *   visitor forward again: the scene starts at rest, with "Replay intro", when
 *   the visitor comes through the step bar's Back (StepBar passes
 *   location.state { via: 'step-back' }) or the browser's Back / Forward (a
 *   POP navigation after another in-app page: StepLayout passes the last
 *   location it showed through the Outlet context; a direct load or a reload
 *   is also a POP, but has no previous location). A replay never moves on by
 *   itself.
 * - One control, product.intro.toggle-play, in every state: its label says
 *   "Pause intro", "Play intro" or "Replay intro"; the scene's
 *   data-trapezium-state (playing | paused | ended) carries the meaning.
 *
 * Every animation uses only transform and opacity. Each element's resting
 * style is its final frame and the keyframes only say where it starts, so the
 * static scene (`is-static`, animation: none) and the end of the animated one
 * are the same picture.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType, useOutletContext } from 'react-router-dom';
import { FastForward, Pause, Play, RotateCcw } from 'lucide-react';
import Screen from '../../demo/Screen';
import { STEPS } from '../../demo/steps';
import BodyFigure from '../../mocks/BodyFigure';
import './Intro.css';

/*
 * Every timing below (and the literal ones in Intro.css) is authored on the
 * original 20 s storyboard and played INTRO_SPEED times faster: JS times go
 * through at(), CSS times are multiplied by --intro-k, and the keyframe
 * percentages are shares of --intro-len, so the whole scene scales as one.
 */
const STORYBOARD_MS = 20000;
export const INTRO_SPEED = 3;
export const INTRO_LENGTH_MS = Math.round(STORYBOARD_MS / INTRO_SPEED); // 6 667 ms
const TIME_SCALE = INTRO_LENGTH_MS / STORYBOARD_MS;
/* A storyboard time (s) as a CSS time on the played timeline. */
const at = (s) => `${+(s * TIME_SCALE).toFixed(3)}s`;
/* The page timer: the scene's length plus a 1 s rest on the final frame. */
export const INTRO_ADVANCE_MS = INTRO_LENGTH_MS + 1000;

const SCREEN_ID = 'product.intro';
const REDUCE = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia(REDUCE).matches;
}

/* ---------- The planets: wordless mini screens (decorative) ---------- */

function ReportPlanet() {
  return (
    <span className="intro-g intro-g--report">
      <span className="intro-g__head">
        <i />
        <i />
      </span>
      <span className="intro-g--report__body">
        <BodyFigure className="intro-g--report__fig" />
        <span className="intro-g--report__rows">
          <i className="is-red" />
          <i />
          <i className="is-green" />
          <i />
          <i className="is-red" />
        </span>
      </span>
      <span className="intro-g--report__band" />
    </span>
  );
}

const RING_R = 15;
const RING_C = 2 * Math.PI * RING_R;

function ScorePlanet() {
  return (
    <span className="intro-g intro-g--score">
      <svg viewBox="0 0 40 40" focusable="false">
        <circle className="intro-g--score__track" cx="20" cy="20" r={RING_R} />
        <circle
          className="intro-g--score__arc"
          cx="20"
          cy="20"
          r={RING_R}
          strokeDasharray={`${0.74 * RING_C} ${RING_C}`}
          transform="rotate(-90 20 20)"
        />
        <circle className="intro-g--score__dot" cx="20" cy="20" r="5" />
      </svg>
      <span className="intro-g__lines">
        <i />
        <i />
      </span>
    </span>
  );
}

function ChatPlanet() {
  return (
    <span className="intro-g intro-g--chat">
      <span className="intro-g--chat__bubble is-them">
        <i />
        <i />
      </span>
      <span className="intro-g--chat__bubble is-me">
        <i />
      </span>
      <span className="intro-g--chat__bubble is-them">
        <i />
        <i />
        <i />
      </span>
    </span>
  );
}

function MessagePlanet() {
  return (
    <span className="intro-g intro-g--msg">
      <span className="intro-g--msg__bar">
        <i />
        <i />
      </span>
      <span className="intro-g--msg__bubble">
        <i />
        <i />
        <span className="intro-g--msg__doc" />
        <span className="intro-g--msg__ticks">
          <svg viewBox="0 0 16 10" focusable="false">
            <path d="M1 5.5 3.8 8 9 2M6.5 8 7 8.4 14.5 1.5" />
          </svg>
        </span>
      </span>
    </span>
  );
}

function ChartPlanet() {
  return (
    <span className="intro-g intro-g--chart">
      <svg viewBox="0 0 60 36" focusable="false">
        <path className="intro-g--chart__band" d="M2 14h56v10H2Z" />
        <path className="intro-g--chart__axis" d="M2 33h56" />
        <polyline className="intro-g--chart__line" points="4,28 16,22 28,25 40,15 52,10" />
        {[
          [4, 28],
          [16, 22],
          [28, 25],
          [40, 15],
          [52, 10],
        ].map(([x, y]) => (
          <circle key={x} className="intro-g--chart__dot" cx={x} cy={y} r="2.2" />
        ))}
      </svg>
    </span>
  );
}

function ChecklistPlanet() {
  return (
    <span className="intro-g intro-g--list">
      {['a', 'b', 'c'].map((k) => (
        <span key={k} className="intro-g--list__row">
          <svg viewBox="0 0 12 12" focusable="false">
            <circle cx="6" cy="6" r="6" />
            <path d="M3.4 6.2 5.2 8l3.4-3.8" />
          </svg>
          <i />
        </span>
      ))}
    </span>
  );
}

/*
 * Orbit: 1 inner, 2 middle, 3 outer (radius as a share of --intro-r). `to` is
 * the resting angle (deg, clockwise from 3 o'clock); each orbit turns through
 * its own sweep as it settles, inner ones further. `delay` is when the planet
 * fades in, `signal` when the engine's pulse reaches it (s).
 */
const ORBITS = { 1: { r: 0.46, sweep: 150 }, 2: { r: 0.74, sweep: 110 }, 3: { r: 1, sweep: 75 } };

const PLANETS = [
  { key: 'report', orbit: 2, to: -122, w: 0.84, h: 1.08, delay: 2.1, signal: 9.5, Glyph: ReportPlanet },
  { key: 'score', orbit: 1, to: -32, w: 0.8, h: 0.8, delay: 3.1, signal: 9.9, Glyph: ScorePlanet },
  { key: 'chat', orbit: 3, to: 14, w: 1, h: 0.8, delay: 4, signal: 12.1, Glyph: ChatPlanet },
  { key: 'message', orbit: 2, to: 58, w: 1, h: 0.82, delay: 4.9, signal: 12.5, Glyph: MessagePlanet },
  { key: 'chart', orbit: 1, to: 150, w: 1.02, h: 0.72, delay: 5.9, signal: 14.8, Glyph: ChartPlanet },
  { key: 'list', orbit: 3, to: 194, w: 0.84, h: 0.86, delay: 6.8, signal: 15.2, Glyph: ChecklistPlanet },
];

/* The engine's three pulses (storyboard s); the CSS heartbeat and bloom keyframes match (44 / 57.3 / 70.6 %). */
const PULSES = [8.8, 11.47, 14.13];

/*
 * The twinkling stars: 100 dots from a fixed seed (the same sky on every
 * visit). Each twinkles a few times within the scene and rests at its base
 * brightness; a handful are larger, with a four-point glint.
 */
const STAR_COUNT = 100;
const SKY = (() => {
  let seed = 7;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  return Array.from({ length: STAR_COUNT }, (_, i) => {
    const t = 2.4 + rand() * 1.6; // one twinkle (s)
    return {
      key: i,
      x: `${(rand() * 100).toFixed(2)}%`,
      y: `${(rand() * 100).toFixed(2)}%`,
      size: `${(1 + rand() * 1.8).toFixed(2)}px`,
      base: (0.35 + rand() * 0.45).toFixed(2),
      t: at(t),
      d: at(rand() * (20 - 4 * t)), // four twinkles end by the storyboard's 20 s
      glint: i % 14 === 0,
    };
  });
})();

/* Slow light streaks sweeping across the sky (start, storyboard s). */
const STREAKS = [
  { key: 'a', at: 1.2, top: '22%', tilt: '-18deg' },
  { key: 'b', at: 9.8, top: '68%', tilt: '-12deg' },
  { key: 'c', at: 16.2, top: '38%', tilt: '-22deg' },
];

/* The sky behind the camera (full-bleed, so outside the scaled scene): two
   still star fields, the twinkling stars and the streaks. It drifts in a
   little less than the scene, for depth. Decorative. */
function Sky() {
  return (
    <span className="intro-sky" aria-hidden="true">
      <span className="intro-stars" />
      <span className="intro-stars intro-stars--far" />
      {SKY.map(({ key, x, y, size, base, t, d, glint }) => (
        <span
          key={key}
          className={`intro-twinkle${glint ? ' is-glint' : ''}`}
          style={{ '--x': x, '--y': y, '--size': size, '--base': base, '--t': t, '--d': d }}
        />
      ))}
      {STREAKS.map(({ key, at: start, top, tilt }) => (
        <span key={key} className="intro-streak" style={{ '--at': at(start), '--top': top, '--tilt': tilt }} />
      ))}
    </span>
  );
}

function Planet({ orbit, to, w, h, delay, signal, name, Glyph }) {
  const { r, sweep } = ORBITS[orbit];
  const style = {
    '--k': r,
    '--a0': `${to - sweep}deg`,
    '--a1': `${to}deg`,
    '--w': w,
    '--h': h,
    '--delay': at(delay),
    '--signal': at(signal),
    '--lit': at(signal + 1.3),
  };
  return (
    <span className={`intro-arm intro-arm--${name}`} style={style}>
      <span className="intro-signal" />
      <span className="intro-pos">
        <span className="intro-upright">
          <span className="intro-planet">
            <Glyph />
          </span>
        </span>
      </span>
    </span>
  );
}

function Scene() {
  return (
    <div className="intro-stage" role="img" aria-label="A glowing engine at the centre, with the screens a patient sees orbiting it">
      <div className="intro-galaxy">
        {/* One depth layer per orbit (its ring and its planets): the outer ones
            push in a little more than the inner, for parallax. */}
        {Object.entries(ORBITS).map(([key, { r }], i) => (
          <span key={key} className="intro-layer" style={{ '--depth': 1 + i * 0.035 }}>
            <span className="intro-ring" style={{ '--k': r, '--delay': at(0.6 + i * 0.4) }} />
            {PLANETS.filter((p) => String(p.orbit) === key).map(({ key: name, ...p }) => (
              <Planet key={name} name={name} {...p} />
            ))}
          </span>
        ))}

        <span className="intro-engine">
          <span className="intro-engine__bloom" />
          <span className="intro-engine__halo" />
          {PULSES.map((t) => (
            <span key={t} className="intro-engine__pulse" style={{ '--delay': at(t) }} />
          ))}
          <span className="intro-engine__core">
            <span className="intro-engine__ring" />
            <span className="intro-engine__beat" />
          </span>
          <span className="intro-engine__flare" />
        </span>
      </div>
    </div>
  );
}

export default function Intro() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const outlet = useOutletContext();
  const next = STEPS[STEPS.findIndex((s) => s.screenId === SCREEN_ID) + 1];

  // Decided once, on arrival: play (forward), rest (back), or static (reduced motion).
  const [arrival] = useState(() => {
    if (prefersReducedMotion()) return 'static';
    const stepBack = location.state?.via === 'step-back';
    // A POP is a browser Back / Forward only if this app showed a page before;
    // a first load (or a reload) is a POP too, with no previous location.
    const previous = outlet?.lastLocationRef?.current;
    const historyBack = navigationType === 'POP' && Boolean(previous) && previous.key !== location.key;
    return stepBack || historyBack ? 'rest' : 'play';
  });
  const reduced = arrival === 'static';
  const [status, setStatus] = useState(arrival === 'play' ? 'playing' : 'ended');
  const [autoAdvance, setAutoAdvance] = useState(arrival === 'play');
  const [atRest, setAtRest] = useState(arrival !== 'play'); // drawn without animation
  const [run, setRun] = useState(0); // a replay remounts the scene, restarting its animations

  const elapsedRef = useRef(0); // ms of the scene already played
  const startedAtRef = useRef(0);

  // The scene's clock: runs only while playing; paused time is not counted.
  useEffect(() => {
    if (status !== 'playing') return undefined;
    startedAtRef.current = Date.now();
    const id = window.setTimeout(() => {
      elapsedRef.current = INTRO_LENGTH_MS;
      setStatus('ended');
    }, Math.max(0, INTRO_LENGTH_MS - elapsedRef.current));
    return () => window.clearTimeout(id);
  }, [status, run]);

  // The page timer: 1 s on the final frame, then on to the next step.
  useEffect(() => {
    if (status !== 'ended' || !autoAdvance) return undefined;
    const id = window.setTimeout(() => navigate(next.route), INTRO_ADVANCE_MS - INTRO_LENGTH_MS);
    return () => window.clearTimeout(id);
  }, [status, autoAdvance, navigate, next.route]);

  const togglePlay = useCallback(() => {
    if (status === 'playing') {
      elapsedRef.current = Math.min(INTRO_LENGTH_MS, elapsedRef.current + Date.now() - startedAtRef.current);
      setStatus('paused');
    } else if (status === 'paused') {
      setStatus('playing');
    }
  }, [status]);

  const replay = useCallback(() => {
    elapsedRef.current = 0;
    setAutoAdvance(false);
    setAtRest(false);
    setRun((r) => r + 1);
    setStatus('playing');
  }, []);

  const skip = useCallback(() => navigate(next.route), [navigate, next.route]);

  const classes = ['intro-scene'];
  if (status === 'paused') classes.push('is-paused');
  if (atRest) classes.push('is-static');

  const ended = status === 'ended';
  let primary = null;
  if (!reduced) {
    // One control with one ID in every state; its label and icon follow the state.
    const label = ended ? 'Replay intro' : status === 'playing' ? 'Pause intro' : 'Play intro';
    const Icon = ended ? RotateCcw : status === 'playing' ? Pause : Play;
    primary = (
      <button
        type="button"
        className="intro-btn"
        data-trapezium-id="product.intro.toggle-play"
        onClick={ended ? replay : togglePlay}
      >
        <Icon size={16} strokeWidth={2.4} aria-hidden="true" />
        {label}
      </button>
    );
  }

  return (
    <Screen id={SCREEN_ID} title="The engine" className="intro">
      <div
        className={classes.join(' ')}
        data-trapezium-state={status}
        style={{ '--intro-len': `${INTRO_LENGTH_MS / 1000}s`, '--intro-k': TIME_SCALE }}
      >
        <div className="intro-controls" role="group" aria-label="Intro">
          <span className="intro-progress" aria-hidden="true">
            <span key={run} />
          </span>
          {primary}
          <button type="button" className="intro-btn" data-trapezium-id="product.intro.skip" onClick={skip}>
            <FastForward size={16} strokeWidth={2.4} aria-hidden="true" />
            Skip intro
          </button>
        </div>

        <div className="intro-body" key={run}>
          <Sky />
          {/* the camera: a slow push-in on the whole scene */}
          <div className="intro-camera">
            <Scene />
            <h1 className="intro-line">
              One engine. <em>Every patient touchpoint.</em>
            </h1>
          </div>
        </div>
      </div>
    </Screen>
  );
}
